import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Loader2, Share2 } from 'lucide-react';
import type { ShareVisibility } from '../types/video';
import { apiClient } from '../lib/apiClient';
import { copyText } from '../lib/clipboard';
import { toast } from '../lib/toast';
import { useShareVisibilityOptions } from '../lib/shareVisibility';
import { broadcastVideosRefresh } from '../hooks/useVideos';
import { ConfirmDialog, DialogShell } from './ConfirmDialog';

interface ShareLinkDialogProps {
  videoId: string;
  /** Token of the working link, or null when the video isn't shared. */
  shareToken: string | null;
  shareExpiresAt?: string | null;
  shareVisibility?: ShareVisibility;
  onClose: () => void;
}

type Expiry = 'never' | '1' | '7' | '30';
const toDays = (value: Expiry) => (value === 'never' ? null : Number(value));

const button: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
};
const primaryButton: React.CSSProperties = {
  ...button, border: 'none', background: 'var(--theme-primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)',
};
const field: React.CSSProperties = {
  borderRadius: 8, fontSize: 13, border: '1px solid var(--border-color, #e5e7eb)',
  background: 'var(--controlsBg, #f9fafb)', color: 'var(--text-primary, #111)',
};

/**
 * Share a video by link. Before a link exists the choices are collected and "Create link" applies
 * them; once it exists every change (who can watch, expiry) saves straight away.
 */
const ShareLinkDialog: React.FC<ShareLinkDialogProps> = ({ videoId, shareToken, shareExpiresAt, shareVisibility, onClose }) => {
  const { t } = useTranslation();
  const options = useShareVisibilityOptions();
  const [token, setToken] = useState(shareToken);
  const [expiresAt, setExpiresAt] = useState(shareToken ? shareExpiresAt ?? null : null);
  const [visibility, setVisibility] = useState<ShareVisibility>(shareVisibility ?? 'public');
  const [newExpiry, setNewExpiry] = useState<Expiry>('never'); // used until the link exists
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);

  const link = token ? `${window.location.origin}/share/${token}` : null;
  const expired = !!expiresAt && Date.parse(expiresAt) <= Date.now();

  /** Runs one change; the library refreshes so the card shows the new state. */
  const save = async (change: () => Promise<void>, failedMessage: string) => {
    setBusy(true);
    setSaved(false);
    try {
      await change();
      setSaved(true);
      broadcastVideosRefresh();
    } catch (err) {
      console.error('[ShareLinkDialog]', err);
      toast.error(failedMessage);
    } finally {
      setBusy(false);
    }
  };
  const saveFailed = t('share_dialog.save_failed', "Couldn't save the change. Please try again.");

  const applyLink = async (days: number | null) => {
    const { data, error } = await apiClient.shareVideo(videoId, days);
    if (error || !data?.shareToken) throw error ?? new Error('No token returned');
    setToken(data.shareToken);
    setExpiresAt(data.shareExpiresAt ?? null);
  };

  const createLink = () =>
    save(async () => {
      await applyLink(toDays(newExpiry));
      const { error } = await apiClient.patchShareVisibility(videoId, visibility);
      if (error) {
        // The link exists but kept its previous visibility.
        setVisibility(shareVisibility ?? 'public');
        throw error;
      }
    }, t('share_link_generate_failed', "Couldn't create the share link. Please try again."));

  const changeVisibility = (value: ShareVisibility) => {
    if (value === visibility) return;
    if (!token) return setVisibility(value);
    const previous = visibility;
    setVisibility(value);
    void save(async () => {
      const { error } = await apiClient.patchShareVisibility(videoId, value);
      if (error) {
        setVisibility(previous);
        throw error;
      }
    }, saveFailed);
  };

  const changeExpiry = (value: Expiry | '') => {
    if (!value) return; // "Expires on …" is the current setting
    if (!token) return setNewExpiry(value);
    void save(() => applyLink(toDays(value)), saveFailed);
  };

  const copyLink = async () => {
    if (!link) return;
    if (!(await copyText(link))) {
      toast.error(t('share_link_copy_failed', "Couldn't copy the link. Please try again."));
      return;
    }
    toast.success(t('share_link_copied'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const turnOff = async () => {
    const { error } = await apiClient.unshareVideo(videoId);
    if (error) {
      toast.error(t('share_link.off_failed', "Couldn't turn off the link. Please try again."));
      return;
    }
    setConfirmOff(false);
    setToken(null);
    setExpiresAt(null);
    setNewExpiry('never');
    setSaved(false);
    broadcastVideosRefresh();
    toast.success(t('share_link.off_done', 'Link turned off.'));
  };

  const expiryLabel = expiresAt
    ? t(expired ? 'share_link.expired_on' : 'share_link.expires_on', {
        date: new Date(expiresAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
      })
    : '';

  return (
    <>
      <DialogShell title={t('video_actions.share')} onCancel={onClose} width={440}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
          <Share2 size={16} style={{ color: 'var(--primary-text, #4f46e5)' }} aria-hidden />
          {t('video_actions.share')}
        </h2>

        {link && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                readOnly
                value={link}
                aria-label={t('share_dialog.link_label', 'Share link')}
                onFocus={(e) => e.currentTarget.select()}
                style={{ ...field, flex: 1, minWidth: 0, padding: '8px 10px', color: 'var(--text-secondary, #6b7280)' }}
              />
              <button type="button" onClick={copyLink} style={primaryButton}>
                {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
                {copied ? t('copied', 'Copied') : t('copy', 'Copy')}
              </button>
            </div>
            {expired && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--theme-danger, #dc2626)' }}>
                {t('share_dialog.expired_hint', 'This link has expired. Choose a new expiry to turn it back on.')}
              </p>
            )}
          </div>
        )}

        <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600 }}>
          {t('share_dialog.who_can_watch', 'Who can watch')}
        </p>
        <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 6, opacity: busy ? 0.7 : 1 }}>
          {options.map((opt) => {
            const active = visibility === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={busy}
                onClick={() => changeVisibility(opt.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 10,
                  textAlign: 'left', cursor: busy ? 'default' : 'pointer', color: 'inherit',
                  background: active ? 'var(--surfaceSelected, rgba(79,70,229,0.1))' : 'transparent',
                  border: active ? '1.5px solid var(--theme-primary, #4f46e5)' : '1.5px solid var(--border-color, #e5e7eb)',
                }}
              >
                <span style={{ color: active ? 'var(--primary-text, #4f46e5)' : 'var(--text-secondary, #6b7280)', display: 'flex' }}>
                  {opt.icon}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: active ? 600 : 500 }}>{opt.label}</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>{opt.desc}</span>
                </span>
              </button>
            );
          })}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 14, fontSize: 13, fontWeight: 600 }}>
          {t('share_link.expiry_label', 'Link expires')}
          <select
            value={token ? (expiresAt ? '' : 'never') : newExpiry}
            disabled={busy}
            onChange={(e) => changeExpiry(e.target.value as Expiry | '')}
            style={{ ...field, padding: '6px 10px', fontWeight: 400, maxWidth: '60%' }}
          >
            {token && expiresAt && <option value="">{expiryLabel}</option>}
            <option value="never">{t('share_link.expiry_never', 'Never')}</option>
            {(['1', '7', '30'] as const).map((days) => (
              <option key={days} value={days}>{t('share_link.expiry_days', { count: Number(days) })}</option>
            ))}
          </select>
        </label>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20 }}>
          {token && (
            <button
              type="button"
              onClick={() => setConfirmOff(true)}
              disabled={busy}
              style={{ ...button, padding: '8px 2px', fontWeight: 500, background: 'none', border: 'none', color: 'var(--theme-danger, #dc2626)' }}
            >
              {t('share_link.turn_off', 'Turn off link')}
            </button>
          )}
          <span
            aria-live="polite"
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}
          >
            {busy && token && <><Loader2 size={13} className="animate-spin" aria-hidden />{t('shared_video.saving', 'Saving…')}</>}
            {!busy && saved && token && <><Check size={13} aria-hidden />{t('share_dialog.saved', 'Saved')}</>}
          </span>
          {token ? (
            <button type="button" onClick={onClose} style={primaryButton}>
              {t('share_dialog.done', 'Done')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                style={{ ...button, fontWeight: 500, background: 'transparent', border: '1px solid var(--border-color, #e5e7eb)', color: 'inherit' }}
              >
                {t('cancel', 'Cancel')}
              </button>
              <button type="button" onClick={createLink} disabled={busy} style={{ ...primaryButton, opacity: busy ? 0.7 : 1 }}>
                {busy && <Loader2 size={14} className="animate-spin" aria-hidden />}
                {t('share_dialog.create_link', 'Create link')}
              </button>
            </>
          )}
        </div>
      </DialogShell>

      {confirmOff && (
        <ConfirmDialog
          danger
          title={t('share_link.off_title', 'Turn off the link?')}
          message={t('share_link.off_message', 'The link stops working for everyone who has it. Sharing again creates a new link.')}
          confirmLabel={t('share_link.turn_off', 'Turn off link')}
          onCancel={() => setConfirmOff(false)}
          onConfirm={turnOff}
        />
      )}
    </>
  );
};

export default ShareLinkDialog;
